import React, { useEffect, useRef, useState } from "react";
import placeholderImage from "@/assets/imgloading.png";

interface LazyLoadProps {
  src?: string;
  lowResSrc?: string;
  alt: string;
  className?: string;
  objectFit?: string;
  blurAmount?: number;
}

const LazyLoad: React.FC<LazyLoadProps> = ({
  src,
  lowResSrc,
  alt,
  className,
  objectFit = "cover",
  blurAmount = 20,
}) => {
  const [loadingStage, setLoadingStage] = useState<
    "placeholder" | "lowRes" | "highRes"
  >("placeholder");
  const [currentSrc, setCurrentSrc] = useState(placeholderImage);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    let observer: IntersectionObserver | null = null; // Declare observer here

    const loadImage = () => {
      // Reset to placeholder (important for updates)
      setLoadingStage("placeholder");
      setCurrentSrc(placeholderImage);

      // Load low-res version first if available
      if (lowResSrc) {
        const lowResImage = new Image();
        lowResImage.src = lowResSrc;
        lowResImage.onload = () => {
          setCurrentSrc(lowResSrc);
          setLoadingStage("lowRes");

          // Load high-res version immediately after low-res
          if (src) {
            const highResImage = new Image();
            highResImage.src = src;
            highResImage.onload = () => {
              setCurrentSrc(src);
              setLoadingStage("highRes");
              if (observer && containerRef.current) {
                observer.unobserve(containerRef.current);
              }
            };
            highResImage.onerror = () => {
              console.error("Error loading high-res image:", src);
              // Handle high-res load failure (optional):
              // You might want to set an error state or show a fallback image here.
              if (observer && containerRef.current) {
                observer.unobserve(containerRef.current);
              }
            };
          }
        };
        lowResImage.onerror = () => {
          //Handle error if it cannot load low res
          console.error("Error loading low-res image:", lowResSrc);
          if (src) {
            const highResImage = new Image();
            highResImage.src = src;
            highResImage.onload = () => {
              setCurrentSrc(src);
              setLoadingStage("highRes");
              if (observer && containerRef.current) {
                observer.unobserve(containerRef.current);
              }
            };
            highResImage.onerror = () => {
              console.error("Error loading high-res image:", src);
              if (observer && containerRef.current) {
                observer.unobserve(containerRef.current);
              }
            };
          }
        };
      } else if (src) {
        //If there isn't a low res, then load the high res
        const highResImage = new Image();
        highResImage.src = src;
        highResImage.onload = () => {
          setCurrentSrc(src);
          setLoadingStage("highRes");
          if (observer && containerRef.current) {
            observer.unobserve(containerRef.current);
          }
        };
        highResImage.onerror = () => {
          console.error("Error loading high-res image:", src);
          if (observer && containerRef.current) {
            observer.unobserve(containerRef.current);
          }
        };
      }
    };

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadImage();
            // Don't unobserve here; unobserve after high-res image loads (or fails)
          }
        });
      },
      { root: null, rootMargin: "-20px", threshold: 0.1 }
    );

    observer.observe(containerRef.current);

    // Cleanup function: Disconnect the observer when the component unmounts *or* when src/lowResSrc change
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [src, lowResSrc]); //  CRITICAL: Add src and lowResSrc as dependencies!

  const getBlurAmount = () => {
    switch (loadingStage) {
      case "placeholder":
        return blurAmount;
      case "lowRes":
        return blurAmount / 2;
      case "highRes":
        return 0;
      default:
        return blurAmount; // Should never happen, but good for safety
    }
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        overflow: "hidden",
        display: "inline-block",
        position: "relative",
      }}
    >
      <img
        src={currentSrc}
        alt={alt}
        style={{
          objectFit: objectFit as React.CSSProperties["objectFit"],
          width: "100%",
          height: "100%",
          display: "block",
          transition: "filter 0.5s ease-out, opacity 0.5s ease-out",
          filter: `blur(${getBlurAmount()}px)`,
          opacity: loadingStage === "placeholder" ? 0.5 : 1, // Good for visual feedback
        }}
      />
    </div>
  );
};

export default LazyLoad;
